import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@contexts/AuthContext';
import { useI18n } from '@/i18n';
import logoWordmark from '../assets/logo-glk-wordmark.png';

export default function Login() {
  const { login } = useAuth();
  const { t }     = useI18n();
  const navigate  = useNavigate();
  const location  = useLocation();
  const from      = location.state?.from?.pathname || '/';

  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) navigate(from, { replace: true });
    else setError(result.error);
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 overflow-hidden">
      {/* Ambient brand backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{
          background:
            'radial-gradient(90% 60% at 50% -10%, rgba(45,125,70,0.18), transparent 60%), ' +
            'radial-gradient(70% 50% at 90% 110%, rgba(26,94,158,0.14), transparent 60%), #F7F8FA',
        }}
      />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm"
      >
        <div className="mb-8 text-center">
          <img src={logoWordmark} alt="GroundLink III Handling" className="h-16 w-auto mx-auto" />
          <p className="mt-3 text-xs font-mono text-gray-400 uppercase tracking-[0.2em]">{t('common.portalName')}</p>
        </div>

        <div className="card p-8 backdrop-blur-sm bg-white/90">
          <h1 className="text-lg font-semibold text-glk-dark mb-1">{t('login.welcome')}</h1>
          <p className="text-sm text-gray-500 mb-6">{t('login.lead')}</p>

          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mb-4 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"
            >
              {error}
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="label">{t('login.email')}</label>
              <input id="email" type="email" autoComplete="email" required className="input"
                     value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div>
              <label htmlFor="password" className="label">{t('login.password')}</label>
              <input id="password" type="password" autoComplete="current-password" required className="input"
                     value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <motion.button
              type="submit" disabled={loading}
              whileTap={{ scale: 0.98 }}
              className="btn-primary w-full mt-2"
            >
              {loading ? t('login.signingIn') : t('login.signIn')}
            </motion.button>
          </form>

          <p className="mt-5 text-center text-sm">
            <Link to="/forgot" className="text-glk-green hover:underline">{t('login.forgot')}</Link>
          </p>
        </div>

        <p className="mt-6 text-center text-[11px] text-gray-400">
          {t('common.footer')}
        </p>
      </motion.div>
    </div>
  );
}
